import { OrbiAppointment } from '@patient-ui/shared/models';

/**
 * Pure helpers for the Orbi Visit History card — the `VisitHistory` return
 * type's renderer. The visits themselves are the same `OrbiAppointment` rows the
 * Upcoming Appointment card renders (mobile passes `UpcomingAppointmentData` to
 * both), so mapping and date formatting are shared; only the heading and the
 * "Rate Visit" destination are specific to this card.
 */

/**
 * The card's heading — "Visit History" at ANY count.
 *
 * Mobile builds it as `'${pluralize('Visit', n)} History'`, so several visits
 * read "Visits History" there, and this followed suit for parity. QA read the two
 * against the web Figma, which labels the head "Visit History" whatever the card
 * holds, and rejected the pluralized form (PCSA-13516). It is a proper section
 * name here, not a count — the same reason the portal's own history page heads
 * itself "Visit History" over a 25-row table.
 *
 * Kept as a function of the count rather than a constant: the signature is what
 * the card binds, and a future design could split the two again.
 */
export function orbiVisitHistoryTitle(_visitCount: number): string {
  return 'Visit History';
}

/**
 * The Rate Your Lab Visit URL for one past visit, or '' when the visit cannot be
 * rated. Mirrors mobile's `buildRateLabVisitUri` +
 * `rateLabVisitParamsFromUpcomingAppointment` and the portal's own
 * `LocationCardComponent.getRateVisitUrl`, which both hit `{base}/rate?…`.
 *
 * Returns '' — so the caller hides the control rather than offering a dead
 * link — unless ALL of these hold:
 *  - a configured base URL (absent in an environment that has no rating host),
 *  - a `locatorId` (the `id` param; the page keys the lab off it), and
 *  - a structured street (`rateVisitAddress`).
 * Mobile applies exactly these three guards and deliberately does NOT fall back
 * to parsing the free-text `locationAddress`, since a mis-split street would
 * prefill the rating form with the wrong lab.
 *
 * The param names are LOWERCASE (`id`, `address`, `city`, `state`, `zip`), and
 * that is load-bearing. They went out capitalized, following mobile's own
 * `rateLabVisitParamsFromUpcomingAppointment` and its comment that the names are
 * case-sensitive — which is true, and is exactly why capitalized was wrong here.
 * The rating page composes its heading from Drupal query tokens, and QA caught it
 * rendering them raw off an agent card: "Rate your experience at
 * [current-page:query:address] [current-page:query:city],
 * [current-page:query:state]". Those tokens name the params in lowercase, the
 * lookup is case-sensitive, so `?Address=` leaves `[current-page:query:address]`
 * unresolved — a literal token where the lab's street belongs.
 *
 * `?address=` is also what BOTH of the portal's own builders send
 * (`LocationCardComponent.getRateVisitUrl`, `LocationDetailsModalComponent`),
 * which is why the Find a Lab flow renders the address correctly from the same
 * page — the divergence was only ever on this surface. Mobile's capitalization
 * is right for whatever endpoint mobile hits; it is not right for this one.
 *
 * Values keep `encodeURIComponent`, so a space is `%20` rather than the `+` that
 * `URLSearchParams` emits. Both work — the location-details modal sends `%20`
 * today — and mobile calls `%20` out as required, so there is no reason to differ.
 */
export function orbiRateVisitUrl(
  visit: OrbiAppointment,
  rateVisitBaseUrl: string | undefined
): string {
  const base = (rateVisitBaseUrl ?? '').trim();
  if (!base) {
    return '';
  }
  if (visit.locatorId === undefined) {
    return '';
  }
  const street = visit.rateVisitAddress?.street?.trim();
  if (!street) {
    return '';
  }

  const params: Record<string, string> = {
    address: street,
    city: visit.rateVisitAddress?.city?.trim() ?? '',
    id: String(visit.locatorId),
    state: visit.rateVisitAddress?.state?.trim() ?? '',
    zip: visit.rateVisitAddress?.postalCode?.trim() ?? '',
  };
  const query = Object.entries(params)
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');

  return `${base}/rate?${query}`;
}
